import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button, Dialog, TextField } from "@/components/ui";
import { customersRepository } from "@/repositories/customersRepository";

/**
 * Adding somebody the shop wants to remember.
 *
 * Four fields, and a credit limit is not one of them. Setting what somebody may
 * owe is a separate decision with a separate permission and its own audit entry
 * — putting it on the create form would let anybody who can capture a name also
 * extend credit, and the record would say "customer added".
 *
 * Only the name is required. A shopkeeper capturing somebody at a counter has
 * whatever the person volunteers, and refusing to save without an email address
 * means the record never gets made.
 */
export function NewCustomerDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  const create = useMutation({
    mutationFn: () =>
      customersRepository.create({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        note: note.trim(),
      }),
    onSuccess: onCreated,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a customer"
      description="Only the name is needed. The rest can follow."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={name.trim().length === 0}
            onClick={() => create.mutate()}
          >
            Add customer
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <TextField
          label="Name"
          value={name}
          maxLength={120}
          onChange={(event) => setName(event.target.value)}
          placeholder="Thabo Mokoena"
        />
        <TextField
          label="Phone number"
          value={phone}
          maxLength={20}
          inputMode="tel"
          onChange={(event) => setPhone(event.target.value)}
          placeholder="082 123 4567"
          hint="Used to reach them about an account or an order. Nothing else."
        />
        <TextField
          label="Email"
          value={email}
          type="email"
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextField
          label="Note"
          value={note}
          maxLength={200}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Buys bread every morning"
        />

        {create.error != null && (
          <p className="text-sm text-danger-700">
            {create.error instanceof Error ? create.error.message : "That could not be saved."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
